import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { ROLES } from '../../shared/schema.js'
import { UI_STRINGS } from './strings.js'

/**
 * Bilingual runtime.
 *
 * Two distinct jobs live here and it is worth keeping them apart:
 *   - `t(key)`  looks up a *UI chrome* string (nav labels, button text) from a
 *     static dictionary that ships with the code.
 *   - `pick(value)` resolves a *content* field that the editor authored as
 *     `{zh, en}`, falling back to the other language when one side is empty so
 *     a half-translated record still renders.
 *
 * Layer rule: logic only. No styling decisions here.
 *
 * 双语运行时。这里有两件不同的事，必须分清：
 *   - t(key) 查「界面文案」（导航、按钮），来自随代码发布的静态词典；
 *   - pick(value) 解析「内容字段」，编辑写成 {zh, en}，当一侧为空时回退到另一侧，
 *     这样只翻译了一半的记录也能正常显示。
 * 分层规则：仅逻辑，不含任何样式决策。
 */

/** Supported language codes. 支持的语言代码。 */
export const LANGUAGES = [
  { code: 'zh', labelZh: '中文', labelEn: '中文', htmlLang: 'zh-CN' },
  { code: 'en', labelZh: 'English', labelEn: 'English', htmlLang: 'en' },
]

const STORAGE_KEY = 'aacvr.lang'
const LanguageContext = createContext(null)

/**
 * Read the initial language: stored choice, then browser preference, then zh.
 *
 * 读取初始语言：已保存的选择 → 浏览器偏好 → 默认中文。
 *
 * @returns {'zh'|'en'} Language code / 语言代码。
 */
function detectInitialLanguage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'zh' || stored === 'en') return stored
  } catch {
    // Private browsing can throw on localStorage access; fall through.
    // 隐私模式下访问 localStorage 可能抛错，直接走后续分支。
  }
  if (typeof navigator !== 'undefined' && !/^zh\b/i.test(navigator.language || '')) return 'en'
  return 'zh'
}

/**
 * Provide the language context to the tree.
 *
 * 向组件树提供语言上下文。
 *
 * @param {{children: React.ReactNode}} props Children / 子节点。
 * @returns {JSX.Element} Provider / 上下文提供者。
 */
export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(detectInitialLanguage)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang)
    } catch {
      // Non-fatal: the choice just will not survive a reload.
      // 非致命：只是刷新后不保留选择。
    }
    const meta = LANGUAGES.find((item) => item.code === lang)
    document.documentElement.setAttribute('lang', meta ? meta.htmlLang : 'zh-CN')
  }, [lang])

  const value = useMemo(() => ({ lang, setLang }), [lang])
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

/**
 * Access the language runtime.
 *
 * 获取语言运行时。
 *
 * @returns {{
 *   lang: 'zh'|'en',
 *   setLang: (code: 'zh'|'en') => void,
 *   toggle: () => void,
 *   t: (key: string, vars?: object) => string,
 *   pick: (value: any) => any,
 *   isZh: boolean,
 * }} Language helpers / 语言工具集。
 */
export function useI18n() {
  const context = useContext(LanguageContext)
  if (!context) throw new Error('useI18n must be used inside <LanguageProvider>')
  const { lang, setLang } = context

  /**
   * Look up a UI string, with `{name}` interpolation.
   *
   * 查界面文案，支持 {name} 占位替换。
   */
  const t = useCallback(
    (key, vars) => {
      const entry = UI_STRINGS[key]
      let text = entry ? entry[lang] : undefined

      // A language may give `{one, other}` instead of a string. Only English
      // needs it here — Chinese has no plural agreement — and only the `n`
      // variable selects, because that is the count every such string counts.
      // Kept in the runtime rather than at the nine call sites of
      // `common.count`: a rule spread across nine places is a rule that will be
      // followed in eight.
      // 某个语言可以给出 `{one, other}` 而不是一个字符串。这里只有英文需要它
      // ——中文没有复数一致——而且只由 `n` 变量决定，因为那正是这类文案所计的数。
      // 规则放在运行时而不是放在 `common.count` 的九个调用点上：
      // 一条摊在九处的规则，最终会被遵守八处。
      if (text && typeof text === 'object') {
        text = Number(vars?.n) === 1 ? text.one ?? text.other : text.other
      }
      // A missing key is a bug, not a runtime failure — surface it visibly in
      // dev and degrade to the key itself in production.
      // 缺 key 是 bug 而非运行时故障——开发期显式暴露，生产环境退化为 key 本身。
      if (text === undefined) {
        if (import.meta.env?.DEV) console.warn(`[i18n] missing string: ${key}`)
        text = key
      }
      if (!vars) return text
      return text.replace(/\{(\w+)\}/g, (match, name) => (name in vars ? String(vars[name]) : match))
    },
    [lang]
  )

  /**
   * Resolve a bilingual content value.
   *
   * Accepts a `{zh, en}` pair, an array pair `{zh: [], en: []}`, or a plain
   * value (returned untouched, e.g. a DOI that has no translation).
   *
   * 解析双语内容值。接受 {zh, en} 对、{zh: [], en: []} 数组对，或普通值
   * （原样返回，例如没有翻译的 DOI）。
   */
  const pick = useCallback(
    (value) => {
      if (value === null || value === undefined) return ''
      if (typeof value !== 'object' || Array.isArray(value)) return value
      if (!('zh' in value) && !('en' in value)) return value

      const other = lang === 'zh' ? 'en' : 'zh'
      const primary = value[lang]
      const fallback = value[other]
      const isEmpty = (candidate) =>
        candidate === undefined ||
        candidate === null ||
        (typeof candidate === 'string' && candidate.trim() === '') ||
        (Array.isArray(candidate) && candidate.length === 0)

      return isEmpty(primary) ? (isEmpty(fallback) ? primary ?? '' : fallback) : primary
    },
    [lang]
  )

  const toggle = useCallback(() => setLang(lang === 'zh' ? 'en' : 'zh'), [lang, setLang])

  return { lang, setLang, toggle, t, pick, isZh: lang === 'zh' }
}

/**
 * Pick a label off a schema entry that carries `labelZh` / `labelEn`.
 *
 * The schema is shared with the backend, so it uses suffixed keys rather than a
 * nested `{zh, en}` object; this adapter keeps call sites tidy.
 *
 * 从带 labelZh / labelEn 的 schema 条目取标签。schema 与后端共用，用的是后缀式
 * 键名而非嵌套 {zh, en}，这个适配器让调用处保持整洁。
 *
 * @param {object} entry Schema entry / schema 条目。
 * @param {'zh'|'en'} lang Language code / 语言代码。
 * @param {string} [prefix] Property prefix, default 'label' / 属性前缀，默认 label。
 * @returns {string} Localised label / 本地化标签。
 */
export function schemaLabel(entry, lang, prefix = 'label') {
  if (!entry) return ''
  const zh = entry[`${prefix}Zh`]
  const en = entry[`${prefix}En`]
  if (lang === 'zh') return zh || en || ''
  return en || zh || ''
}

/** UI string key per role, keyed off ROLES itself. 每个角色对应的文案 key，键名取自 ROLES。 */
const ROLE_LABEL_KEYS = {
  [ROLES.ADMIN]: 'admin.roleAdmin',
  [ROLES.EDITOR]: 'admin.roleEditor',
  [ROLES.AGENT]: 'admin.roleAgent',
}

/**
 * Name a role for display.
 *
 * There are three roles now, so `isAdmin ? 'Admin' : 'Editor'` — the shape every
 * role badge in the console used to have — stopped telling the truth the moment
 * `agent` existed: it labels an agent account "Editor", and that is precisely
 * the account whose limits a reader most needs to see named correctly. A lookup
 * answers for whatever roles the table holds, and an unknown role falls back to
 * its own raw name rather than to somebody else's.
 *
 * 给角色取一个可展示的名字。
 *
 * 现在有三个角色了，后台各处角色徽章原本的写法 `isAdmin ? 管理员 : 编辑`，
 * 在 `agent` 出现的那一刻起就不再说真话：它会把 agent 账号标成「编辑」，
 * 而恰恰是这个账号的边界最需要被正确叫出名字。查表能对「表里现有的所有角色」
 * 作答；遇到未知角色就退回它自己的原始名字，而不是退到别人头上。
 *
 * @param {string} role Role name from ROLES / ROLES 中的角色名。
 * @param {(key: string) => string} t UI string lookup / 界面文案查询函数。
 * @returns {string} Localised role name / 本地化角色名称。
 */
export function roleLabel(role, t) {
  const key = ROLE_LABEL_KEYS[role]
  return key ? t(key) : String(role ?? '')
}
